import { GutXAxisOrgan, OrganLookupCode } from '@repo/types';
import { GutXAxisOrganBadge, Typography } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { findAcknowledgment } from 'helpers/acknowledgments';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useMemo } from 'react';
import useAcknowledgments from 'services/acknowledgments/useAcknowledgments';
import useFeature from 'services/features/useFeature';
import { AcknowledgmentResource } from 'types/Acknowledgment';
import { organAnchorId } from 'views/Results/utils/organs';
import FlaggedMetricsCount from '../FlaggedMetricsCount';
import ConditionLinkCard from './ConditionLinkCard';
import styles from './OrganSection.module.css';
import { sortConditionsReportedFirst } from './utils/sortConditions';

type OrganSectionProps = {
  organ: GutXAxisOrgan;
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** Every code the map draws, so the badge crops the same gut. */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
};

export default function OrganSection({
  organ,
  age,
  pregnant,
  sex,
  drawnCodes,
}: OrganSectionProps) {
  const { trackEvent } = useAnalytics();

  const { isFeatureEnabled: isSensitiveModalEnabled } = useFeature(
    'sensitive_conditions_reminder_modal',
  );

  const conditions = useMemo(
    () => sortConditionsReportedFirst(organ.conditions),
    [organ.conditions],
  );

  const { data: acknowledgments } = useAcknowledgments();

  const { currentTinyAccountId, currentKitId } = useCurrentKitAndTinyAccount();

  return (
    <div className={styles.container} id={organAnchorId(organ.lookup_code)}>
      <div className={styles.header}>
        <GutXAxisOrganBadge
          organ={organ}
          age={age}
          pregnant={pregnant}
          sex={sex}
          drawnCodes={drawnCodes}
        />
        <Typography variant='heading-m'>{organ.display_name}</Typography>
      </div>
      <div className={styles.conditionsCards}>
        {conditions.map(condition => {
          const acknowledgment = findAcknowledgment(
            acknowledgments,
            AcknowledgmentResource.CONDITIONS_METRICS,
            `${condition.id}`,
            currentTinyAccountId ?? '',
            currentKitId ?? '',
          );

          return (
            <ConditionLinkCard
              key={condition.id}
              name={condition.display_name}
              href={condition.condition}
              markedInSurvey={condition.marked_in_survey}
              onClick={
                condition.marked_in_survey
                  ? () =>
                      trackEvent('Survey Flagged Condition Clicked', {
                        condition: condition.condition,
                      })
                  : undefined
              }
              trailing={
                <FlaggedMetricsCount
                  flagged={condition.flagged_count}
                  total={condition.total_count}
                  sensitive={
                    condition.sensitive &&
                    !acknowledgment &&
                    isSensitiveModalEnabled
                  }
                />
              }
            />
          );
        })}
      </div>
    </div>
  );
}
